import { type MatterStatusCategoryOption } from "@/lib/data/static/matterStatics";
import { getNextMatterStatute } from "@/lib/data/static/matters/statuteStatics";
import type {
  Contact,
  Matter,
  MatterIncidentReport,
  MatterInsurance,
  MatterSettlementDemand,
  MatterStatus,
  MatterStatusPhase,
  MatterTask,
  MedicalTreatment,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getInsuranceContactConnections } from "@/lib/utils/connections/insurance-contact-connections";
import { calculateAge } from "@/lib/utils/dateUtils";
import {
  formatContactsName,
  formatDate,
  formatInsuranceTitle,
  formatNumericDate,
} from "@/lib/utils/format";
import { calculateLostWages } from "@/lib/utils/math";
import { getIsFileImage, isCriticalImage } from "@/lib/utils/matterFile";
import {
  compareMatterDefendantConnectionsDisplayOrder,
  getMatterContactConnections,
  getThirdPartyMatterContactIds,
  isDefendantPartyConnectionLabel,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { getMedicalTreatmentContactConnection } from "@/lib/utils/connections/medical-treatment-contact-connections";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { getCalendarDaysUntilStatute } from "@/lib/utils/matters/statute-urgency-utils";
import dayjs from "dayjs";
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

type MatterFileWithNewKeys = Matter["files"][number];

export const getMatterFiles = (matter: Matter): MatterFileWithNewKeys[] =>
  matter.files;

export const getMatterFileName = (file: MatterFileWithNewKeys) => file.name;

export const getMatterFilePresignedUrlKey = (file: MatterFileWithNewKeys) =>
  file.presignedUrlKey ?? "";

export const getMatterFileIsFolder = (file: MatterFileWithNewKeys) =>
  file.isFolder ?? false;

const normalizeMatterFileForImage = (
  file: MatterFileWithNewKeys,
): MatterFileWithNewKeys => file;

export { currencyFormatter };

export function formatCurrency(value?: number | null) {
  const normalizedValue = Number(value);
  if (value == null || !Number.isFinite(normalizedValue)) {
    return "—";
  }

  return currencyFormatter.format(normalizedValue);
}

export function formatDateWithRelativeDays(value?: string | null) {
  if (!value || !dayjs(value).isValid()) {
    return "—";
  }

  const formattedDate = formatDate(value, false);
  const daysAgo = Math.max(dayjs().diff(dayjs(value), "day"), 0);

  return `${formattedDate} (${daysAgo} days ago)`;
}

export type OverviewSummarySegment = {
  label: string;
  value: string;
};

export function getOverviewClientSummarySegments(
  matter: Matter,
  client?: Contact | null,
): OverviewSummarySegment[] {
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const segments: OverviewSummarySegment[] = [];

  const clientName =
    (client ? formatContactsName(client) : undefined) ??
    (clientConnection?.contact
      ? formatContactsName(clientConnection.contact)
      : undefined);
  const gender = client?.individualGender;
  const age = client?.individualDateOfBirth
    ? calculateAge(String(client.individualDateOfBirth))
    : undefined;
  const languages = [
    client?.individualPrimaryLanguage,
    ...(client?.individualSecondaryLanguage ?? []),
  ].filter(Boolean) as string[];
  const language = languages.length > 0 ? languages.join(", ") : undefined;
  if (clientName) {
    segments.push({ label: "Name", value: clientName });
  }
  if (gender) {
    segments.push({ label: "Gender", value: gender });
  }
  if (age != null) {
    segments.push({ label: "Age", value: `${String(age)} Y/O` });
  }
  if (language) {
    segments.push({ label: "Language", value: language });
  }

  const dateOfBirth = formatNumericDate(
    client?.individualDateOfBirth as string | Date | undefined,
  );
  if (dateOfBirth) {
    segments.push({ label: "DOB", value: dateOfBirth });
  }

  const addressSource =
    client?.addresses ?? clientConnection?.contact.addresses ?? [];
  const states = [
    ...new Set(addressSource.map((address) => address.state).filter(Boolean)),
  ];
  if (states.length > 0) {
    segments.push({ label: "Residence", value: states.join(", ") });
  }

  return segments;
}

export function getOverviewIncidentSummarySegments(
  matter: Matter,
): OverviewSummarySegment[] {
  const segments: OverviewSummarySegment[] = [];
  const dateOfLoss = formatDate(matter.dateOfLoss);
  if (dateOfLoss) {
    segments.push({ label: "Date of loss", value: dateOfLoss });
  }

  const incidentState = matter.incidentAddressState?.trim();
  if (incidentState) {
    segments.push({ label: "Incident state", value: incidentState });
  }

  return segments;
}

export function getLatestByDate<T>(
  items: T[],
  getDate: (item: T) => string | null | undefined,
): T | undefined {
  return getItemsByDate(items, getDate)[0];
}

export function getSentSettlementDemands(
  matter: Matter,
): MatterSettlementDemand[] {
  return getItemsByDate(matter.settlementDemands, (demand) => demand.dateSent);
}

function getItemsByDate<T>(
  items: T[],
  getDate: (item: T) => string | null | undefined,
): T[] {
  return [...items]
    .filter((item) => {
      const value = getDate(item);
      return Boolean(value && dayjs(value).isValid());
    })
    .sort(
      (a, b) =>
        dayjs(getDate(b) ?? undefined).valueOf() -
        dayjs(getDate(a) ?? undefined).valueOf(),
    );
}

export function getHeroImage(matter: Matter) {
  const files = getMatterFiles(matter).map(normalizeMatterFileForImage);

  return (
    files.find(
      (file) =>
        !getMatterFileIsFolder(file) &&
        getIsFileImage(getMatterFileName(file)) &&
        isCriticalImage(file),
    ) ??
    files.find(
      (file) =>
        !getMatterFileIsFolder(file) && getIsFileImage(getMatterFileName(file)),
    )
  );
}

export function getActiveAssignees(matter: Matter) {
  return matter.assignees
    .filter((assignee) => !assignee.dateTimeEnd)
    .sort((a, b) => {
      const order = ["Lead Attorney", "Case Manager", "Legal Assistant"];
      const aIndex = order.indexOf(a.role.label);
      const bIndex = order.indexOf(b.role.label);

      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
}

export function getActiveTeams(matter: Matter) {
  return matter.teamConnections.filter((team) => !team.dateTimeEnd);
}

export function getProviderName(_matter: Matter, treatment: MedicalTreatment) {
  const providerConnection = getMedicalTreatmentContactConnection(
    treatment,
    "Provider",
  );
  return providerConnection
    ? getConnectionContactName(providerConnection.contact)
    : "Provider not assigned";
}

export function getPrimaryThirdPartyInsurance(matter: Matter) {
  const defendantConnections = getMatterContactConnections(matter)
    .filter((connection) => isDefendantPartyConnectionLabel(connection.label))
    .sort(compareMatterDefendantConnectionsDisplayOrder);

  const orderedDefendantContactIds = [
    ...new Set(defendantConnections.map((connection) => connection.contactId)),
  ];
  const thirdPartyContactIds = getThirdPartyMatterContactIds(matter);
  const orderedOtherPartyContactIds = getMatterContactConnections(matter)
    .filter(
      (connection) =>
        thirdPartyContactIds.has(connection.contactId) &&
        !orderedDefendantContactIds.includes(connection.contactId),
    )
    .map((connection) => connection.contactId);
  const orderedThirdPartyContactIds = [
    ...orderedDefendantContactIds,
    ...new Set(orderedOtherPartyContactIds),
  ];

  for (const defendantContactId of orderedThirdPartyContactIds) {
    const matchingInsurance = matter.insurances.find((insurance) =>
      [
        ...getInsuranceContactConnections(insurance, "Insured"),
        ...getInsuranceContactConnections(insurance, "Policy Holder"),
      ].some((connection) => connection.targetObjectId === defendantContactId),
    );

    if (matchingInsurance) {
      return matchingInsurance;
    }
  }

  return undefined;
}

export function getPrimaryFirstPartyInsurance(matter: Matter) {
  const clientId = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  )?.contactId;

  if (!clientId) {
    return undefined;
  }

  return matter.insurances.find((insurance) =>
    getInsuranceContactConnections(insurance, "Insured").some(
      (connection) => connection.targetObjectId === clientId,
    ),
  );
}

export function getClientFirstPartyAutoInsurances(matter: Matter) {
  const clientId = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  )?.contactId;

  if (!clientId) {
    return [];
  }

  return matter.insurances.filter((insurance) => {
    if (insurance.type !== "Auto") {
      return false;
    }

    if (insurance.coverageEnabled === false) {
      return false;
    }

    return getInsuranceContactConnections(insurance, "Insured").some(
      (connection) => connection.targetObjectId === clientId,
    );
  });
}

export function getPendingDemandThirdPartyInsurances(
  matter: Matter,
): MatterInsurance[] {
  const thirdPartyContactIds = getThirdPartyMatterContactIds(matter);

  return matter.insurances.filter((insurance) => {
    if (insurance.coverageEnabled === false) {
      return false;
    }

    return [
      ...getInsuranceContactConnections(insurance, "Insured"),
      ...getInsuranceContactConnections(insurance, "Policy Holder"),
    ].some((connection) => thirdPartyContactIds.has(connection.targetObjectId));
  });
}

export function getInvestigationThirdPartyInsurances(
  matter: Matter,
): MatterInsurance[] {
  return getPendingDemandThirdPartyInsurances(matter).filter(
    (insurance) => insurance.type !== "Health",
  );
}

export function getInvestigationFirstPartyInsurances(
  matter: Matter,
): MatterInsurance[] {
  const clientId = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  )?.contactId;

  if (!clientId) {
    return [];
  }

  return matter.insurances.filter((insurance) => {
    if (insurance.type === "Health") {
      return false;
    }

    if (insurance.coverageEnabled === false) {
      return false;
    }

    return getInsuranceContactConnections(insurance, "Insured").some(
      (connection) => connection.targetObjectId === clientId,
    );
  });
}

export type InvestigationCoverageStatusTone =
  | "green"
  | "gray"
  | "amber"
  | "red";

export type InvestigationCoverageBadge = {
  label: string;
  tone: InvestigationCoverageStatusTone;
};

export type InvestigationCoverageSubitem = {
  id: string;
  label: string;
  status?: string;
  statusTone?: InvestigationCoverageStatusTone;
  note?: string;
  badges?: InvestigationCoverageBadge[];
};

export type InvestigationCoverageRow = {
  key: string;
  label: string;
  status?: string;
  statusTone?: InvestigationCoverageStatusTone;
  note?: string;
  badges?: InvestigationCoverageBadge[];
  subitems?: InvestigationCoverageSubitem[];
};

type UmUimCoverageType = "um" | "uim";

export function getInvestigationCoverageStatusBadgeClass(
  statusTone: InvestigationCoverageStatusTone,
) {
  return cn(
    "rounded-full px-3 py-1 text-xs font-medium",
    statusTone === "green" && "bg-emerald-100 text-emerald-700",
    statusTone === "gray" && "bg-muted text-muted-foreground",
    statusTone === "amber" && "bg-amber-100 text-amber-800",
    statusTone === "red" && "bg-red-100 text-red-700",
  );
}

function getIncidentReportStatusTone(
  status?: MatterIncidentReport["status"],
): InvestigationCoverageStatusTone {
  switch (status) {
    case "Received":
      return "green";
    case "Requested":
      return "amber";
    case "Not Requested":
      return "gray";
    default:
      return "gray";
  }
}

function getIncidentReportSentimentTone(
  sentiment?: MatterIncidentReport["sentiment"],
): InvestigationCoverageStatusTone {
  switch (sentiment) {
    case "Favorable":
      return "green";
    case "Unfavorable":
      return "red";
    case "Inconclusive":
      return "amber";
    default:
      return "gray";
  }
}

function getIncidentReportBadges(
  report: MatterIncidentReport,
): InvestigationCoverageBadge[] {
  const badges: InvestigationCoverageBadge[] = [];

  if (report.status) {
    badges.push({
      label: report.status,
      tone: getIncidentReportStatusTone(report.status),
    });
  }

  if (report.sentiment) {
    badges.push({
      label: report.sentiment,
      tone: getIncidentReportSentimentTone(report.sentiment),
    });
  }

  return badges;
}

function getPolicyLimitConfirmationDisplay(
  insurance: MatterInsurance,
): Pick<InvestigationCoverageSubitem, "status" | "statusTone" | "note"> {
  return {
    status: insurance.policyLimitConfirmation ? "Confirmed" : "Pending",
    statusTone: insurance.policyLimitConfirmation ? "green" : "gray",
    note:
      insurance.policyLimitPerPerson != null
        ? formatCurrency(insurance.policyLimitPerPerson)
        : "No limit tracked",
  };
}

function getThirdPartyCoverageStatusTone(
  status?: MatterInsurance["coverage"],
): InvestigationCoverageStatusTone {
  switch (status) {
    case "Confirmed":
      return "green";
    case "Denied":
      return "red";
    case "Pending":
      return "amber";
    case "Unconfirmed":
    default:
      return "gray";
  }
}

function getThirdPartyCoverageBadges(
  insurance?: MatterInsurance,
): InvestigationCoverageBadge[] {
  const coverage = insurance?.coverage?.trim();

  return [
    {
      label: coverage || "Missing",
      tone: getThirdPartyCoverageStatusTone(insurance?.coverage),
    },
  ];
}

export function buildThirdPartyCoverageRow(
  matter: Matter,
  insurances: MatterInsurance[] = getInvestigationThirdPartyInsurances(matter),
): InvestigationCoverageRow {
  if (insurances.length === 0) {
    return {
      key: "third-party-coverage",
      label: "Third-party coverage",
      badges: getThirdPartyCoverageBadges(),
    };
  }

  if (insurances.length === 1) {
    return {
      key: "third-party-coverage",
      label: "Third-party coverage",
      badges: getThirdPartyCoverageBadges(insurances[0]),
    };
  }

  return {
    key: "third-party-coverage",
    label: "Third-party coverage",
    subitems: insurances.map((insurance) => ({
      id: insurance.id,
      label: getInsuranceCarrierName(matter, insurance),
      badges: getThirdPartyCoverageBadges(insurance),
    })),
  };
}

export function buildPolicyLimitConfirmationCoverageRow(
  matter: Matter,
): InvestigationCoverageRow {
  const clientIds = new Set(
    getMatterContactConnections(matter)
      .filter(
        (connection) => connection.label.trim().toLowerCase() === "client",
      )
      .map((connection) => connection.contactId),
  );
  // Match the policy-limit evaluator: only Client-as-Insured establishes ownership.
  const insurances = matter.insurances.filter(
    (insurance) =>
      insurance.coverageEnabled !== false &&
      !getInsuranceContactConnections(insurance, "Insured").some((connection) =>
        clientIds.has(connection.targetObjectId),
      ),
  );
  if (insurances.length === 0) {
    return {
      key: "policy-limit-search",
      label: "Policy limit confirmation",
      status: "Pending",
      statusTone: "gray",
      note: "No limit tracked",
    };
  }

  if (insurances.length === 1) {
    return {
      key: "policy-limit-search",
      label: "Policy limit confirmation",
      ...getPolicyLimitConfirmationDisplay(insurances[0]),
    };
  }

  return {
    key: "policy-limit-search",
    label: "Policy limit confirmation",
    subitems: insurances.map((insurance) => ({
      id: insurance.id,
      label: getInsuranceCarrierName(matter, insurance),
      ...getPolicyLimitConfirmationDisplay(insurance),
    })),
  };
}

export function buildTcrCoverageRow(matter: Matter): InvestigationCoverageRow {
  const incidentReports = matter.incidentReports;

  if (incidentReports.length === 0) {
    return {
      key: "tcr",
      label: "Incident report",
      badges: [{ label: "Missing", tone: "gray" }],
    };
  }

  if (incidentReports.length === 1) {
    const badges = getIncidentReportBadges(incidentReports[0]);

    return {
      key: "tcr",
      label: "Incident report",
      badges: badges.length > 0 ? badges : [{ label: "Missing", tone: "gray" }],
    };
  }

  return {
    key: "tcr",
    label: "Incident report",
    subitems: incidentReports.map((report) => {
      const badges = getIncidentReportBadges(report);

      return {
        id: report.id,
        label: report.description?.trim() || "Incident report",
        badges:
          badges.length > 0 ? badges : [{ label: "Missing", tone: "gray" }],
      };
    }),
  };
}

function getUmUimCoverageDisplay(
  insurance: MatterInsurance,
  type: UmUimCoverageType,
): Pick<InvestigationCoverageSubitem, "status" | "statusTone" | "note"> {
  const exists = type === "um" ? insurance.umExists : insurance.uimExists;
  const perPerson =
    type === "um" ? insurance.umPerPerson : insurance.uimPerPerson;
  const perIncident =
    type === "um" ? insurance.umPerIncident : insurance.uimPerIncident;

  if (!exists) {
    return { status: "None", statusTone: "gray", note: "" };
  }

  if (perPerson == null && perIncident == null) {
    return { status: "Present - No Value", statusTone: "amber", note: "" };
  }

  return {
    status: "Confirmed",
    statusTone: "green",
    note: `${formatCurrency(perPerson)}/${formatCurrency(perIncident)}`,
  };
}

export function buildFirstPartyUmUimCoverageRow(
  matter: Matter,
  insurances: MatterInsurance[],
  label: string,
  type: UmUimCoverageType,
): InvestigationCoverageRow {
  if (insurances.length === 1) {
    const display = getUmUimCoverageDisplay(insurances[0], type);

    return {
      key: label,
      label,
      ...display,
    };
  }

  return {
    key: label,
    label,
    subitems: insurances.map((insurance) => ({
      id: insurance.id,
      label: getInsuranceCarrierName(matter, insurance),
      ...getUmUimCoverageDisplay(insurance, type),
    })),
  };
}

export function getTopStats(matter: Matter) {
  const primaryStatute = getNextMatterStatute(matter);
  const statuteDate = primaryStatute?.date;
  const statuteDaysRemaining = getCalendarDaysUntilStatute(statuteDate);
  const thirdPartyInsurance = getPrimaryThirdPartyInsurance(matter);
  const firstPartyInsurance = getPrimaryFirstPartyInsurance(matter);
  const daysOnDesk = matter.dateCreated
    ? dayjs().diff(dayjs(matter.dateCreated), "day")
    : null;

  return {
    statuteDate,
    statuteDaysRemaining,
    statuteId: primaryStatute?.id,
    statuteConfirmed: primaryStatute?.confirmed,
    liabilityValue: formatLiabilityStatusLabel(thirdPartyInsurance),
    liabilityDetail: getInsuranceCoverageStatusLabel(thirdPartyInsurance),
    liabilityPolicyLimitPerPerson: thirdPartyInsurance?.policyLimitPerPerson,
    liabilityPolicyLimitPerIncident:
      thirdPartyInsurance?.policyLimitPerIncident,
    clientUmPerPerson: firstPartyInsurance?.umExists
      ? firstPartyInsurance.umPerPerson
      : null,
    clientUmPerIncident: firstPartyInsurance?.umExists
      ? firstPartyInsurance.umPerIncident
      : null,
    clientUimPerPerson: firstPartyInsurance?.uimExists
      ? firstPartyInsurance.uimPerPerson
      : null,
    clientUimPerIncident: firstPartyInsurance?.uimExists
      ? firstPartyInsurance.uimPerIncident
      : null,
    clientMedPayExists: Boolean(
      firstPartyInsurance &&
        (firstPartyInsurance.medPay || firstPartyInsurance.medPayLimit != null),
    ),
    clientMedPayLimit: firstPartyInsurance?.medPayLimit ?? null,
    daysOnDesk,
  };
}

export function getInsuranceCarrierName(
  _matter: Matter,
  insurance?: MatterInsurance,
): string {
  if (!insurance) {
    return "Missing";
  }

  return (
    formatInsuranceTitle({
      insurance,
    })?.insurerPart ?? "Unknown insurer"
  );
}

export function getInsuranceCoverageStatusLabel(
  insurance?: MatterInsurance,
): string {
  if (!insurance) {
    return "-";
  }

  if (insurance.coverageEnabled === false) {
    return "No Coverage";
  }

  return insurance.coverage || "-";
}

export function formatLiabilityStatusLabel(
  insurance?: MatterInsurance,
): string {
  const liability = insurance?.liability?.trim();

  if (!liability) {
    return "-";
  }

  if (
    ["Accepted", "Partially Accepted"].includes(liability) &&
    insurance?.liabilityPercentage != null &&
    (liability === "Partially Accepted" ||
      Number(insurance.liabilityPercentage) !== 100)
  ) {
    return `${liability} - ${insurance.liabilityPercentage}%`;
  }

  return liability;
}

export function getLiabilityToneClass(
  liability?: MatterInsurance["liability"],
): string {
  switch (liability?.trim()) {
    case "Accepted":
      return "text-emerald-700";
    case "Partially Accepted":
      return "text-amber-600";
    case "Denied":
      return "text-red-600";
    case "Pending":
      return "text-amber-600";
    default:
      return "text-foreground";
  }
}

export function getStatusToneClass(status?: string | null): string {
  switch (status?.trim().toLowerCase()) {
    case "unconfirmed":
      return "text-muted-foreground";
    case "pending":
      return "text-amber-600";
    case "confirmed":
      return "text-emerald-700";
    case "denied":
      return "text-red-600";
    default:
      return "text-foreground";
  }
}

export function getGoalChipToneClass(
  elapsedDays?: number | null,
  goalDays?: number | null,
): string {
  if (elapsedDays == null || goalDays == null || goalDays <= 0) {
    return "bg-muted text-muted-foreground";
  }

  if (elapsedDays > goalDays) {
    return "bg-red-100 text-red-700";
  }

  if (elapsedDays >= goalDays * 0.75) {
    return "bg-amber-100 text-amber-700";
  }

  return "bg-emerald-100 text-emerald-700";
}

function isMedicalRecordReceivedOrNotApplicable(status?: string | null) {
  return status === "Received" || status === "N/A";
}

function isProviderRecordsComplete(
  matter: Matter,
  treatment: Matter["medicalTreatments"][number],
) {
  if (treatment.recordNotApplicable) {
    return true;
  }

  const records = matter.medicalTreatmentRecords.filter(
    (record) => record.medicalTreatmentId === record.id,
  );

  return (
    records.length > 0 &&
    records.every((record) =>
      isMedicalRecordReceivedOrNotApplicable(record.status),
    )
  );
}

function isProviderBillsComplete(
  matter: Matter,
  treatment: Matter["medicalTreatments"][number],
) {
  if (treatment.billNotApplicable) {
    return true;
  }

  const bills = matter.medicalTreatmentBills.filter(
    (bill) => bill.medicalTreatmentId === bill.id,
  );

  return (
    bills.length > 0 &&
    bills.every((bill) =>
      isMedicalRecordReceivedOrNotApplicable(bill.recordStatus),
    )
  );
}

export function getTreatmentSummary(matter: Matter) {
  const providers = matter.medicalTreatments;
  const recordsReceived = providers.filter((treatment) =>
    isProviderRecordsComplete(matter, treatment),
  ).length;
  const billsReceived = providers.filter((treatment) =>
    isProviderBillsComplete(matter, treatment),
  ).length;
  const balancesConfirmed = providers.filter((treatment) =>
    matter.medicalTreatmentBalanceConfirmations.some(
      (confirmation) =>
        confirmation.medicalTreatmentId === treatment.id &&
        confirmation.confirmed,
    ),
  ).length;
  const {
    totalBill: totalBilled,
    totalPaid,
    totalOutstanding,
  } = calculateMedicalTreatmentBillTotals(matter.medicalTreatmentBills);

  return {
    providersCount: providers.length,
    recordsReceived,
    billsReceived,
    balancesConfirmed,
    totalBilled,
    totalPaid,
    totalOutstanding,
  };
}

export type EconomicDamageLineStatusTone = "confirmed" | "warning" | "muted";

export type EconomicDamageLine = {
  label: string;
  amount: number;
  statusTone: EconomicDamageLineStatusTone;
  statusLabel: string;
};

function getMedicalSpecialsDamageStatus(
  treatmentSummary: ReturnType<typeof getTreatmentSummary>,
): Pick<EconomicDamageLine, "statusTone" | "statusLabel"> {
  const { providersCount, balancesConfirmed } = treatmentSummary;

  if (providersCount === 0) {
    return {
      statusTone: "muted",
      statusLabel: "No providers added",
    };
  }

  if (balancesConfirmed === providersCount) {
    return {
      statusTone: "confirmed",
      statusLabel: "Confirmed",
    };
  }

  return {
    statusTone: "warning",
    statusLabel: `${balancesConfirmed} of ${providersCount} providers confirmed`,
  };
}

function getSimpleDamageCategoryStatus(
  itemCount: number,
): Pick<EconomicDamageLine, "statusTone" | "statusLabel"> {
  if (itemCount === 0) {
    return {
      statusTone: "muted",
      statusLabel: "Not added",
    };
  }

  return {
    statusTone: "confirmed",
    statusLabel: "Confirmed",
  };
}

export function getMatterEconomicDamagesSummary(matter: Matter) {
  const treatmentSummary = getTreatmentSummary(matter);
  const toFiniteNumber = (value: unknown) => {
    const normalizedValue = Number(value);
    return Number.isFinite(normalizedValue) ? normalizedValue : 0;
  };
  const medicalSpecials = toFiniteNumber(
    calculateMedicalTreatmentBillTotals(matter.medicalTreatmentBills).totalBill,
  );
  const lostWages = matter.lostWages.reduce(
    (sum, lostWage) =>
      sum +
      toFiniteNumber(
        calculateLostWages(
          lostWage.positionType,
          lostWage.positionRate,
          lostWage.timeMissed,
        ),
      ),
    0,
  );
  const propertyDamage = matter.propertyDamages.reduce(
    (sum, damage) => sum + toFiniteNumber(damage.originalAmount),
    0,
  );

  const total = [medicalSpecials, lostWages, propertyDamage].reduce(
    (sum, amount) => sum + toFiniteNumber(amount),
    0,
  );

  return {
    total,
    lines: [
      {
        label: "Medical specials",
        amount: medicalSpecials,
        ...getMedicalSpecialsDamageStatus(treatmentSummary),
      },
      {
        label: "Lost wages",
        amount: lostWages,
        ...getSimpleDamageCategoryStatus(matter.lostWages.length),
      },
      {
        label: "Property damage",
        amount: propertyDamage,
        ...getSimpleDamageCategoryStatus(matter.propertyDamages.length),
      },
    ] satisfies EconomicDamageLine[],
  };
}

export function getIsTreatmentSubstantiallyComplete(matter: Matter) {
  const summary = getTreatmentSummary(matter);

  return (
    summary.providersCount > 0 &&
    summary.recordsReceived === summary.providersCount &&
    summary.billsReceived === summary.providersCount
  );
}

export function getLatestAcceptedSettlement(matter: Matter) {
  const latestAcceptedOffer = getLatestByDate(
    matter.settlementOffers.filter((offer) => offer.accepted),
    (offer) => offer.acceptedDate ?? offer.dateReceived,
  );
  const latestAcceptedCounter = getLatestByDate(
    matter.settlementCounters.filter((counter) => counter.accepted),
    (counter) => counter.acceptedDate ?? counter.dateSent,
  );

  if (!latestAcceptedOffer) {
    return latestAcceptedCounter;
  }

  if (!latestAcceptedCounter) {
    return latestAcceptedOffer;
  }

  const latestOfferDate =
    latestAcceptedOffer.acceptedDate ?? latestAcceptedOffer.dateReceived;
  const latestCounterDate =
    latestAcceptedCounter.acceptedDate ?? latestAcceptedCounter.dateSent;

  return dayjs(latestOfferDate).isAfter(dayjs(latestCounterDate))
    ? latestAcceptedOffer
    : latestAcceptedCounter;
}

export function getCompletedTreatmentProvidersCount(matter: Matter) {
  const today = dayjs().startOf("day");

  return matter.medicalTreatments.filter((treatment) => {
    const latestAppointment = getLatestByDate(
      matter.medicalTreatmentAppointments.filter(
        (appointment) =>
          appointment.medicalTreatmentId === treatment.id &&
          appointment.outcome !== "missed" &&
          appointment.outcome !== "scheduled",
      ),
      (appointment) => appointment.date,
    );
    const hasPastAppointment = Boolean(
      latestAppointment?.date &&
        dayjs(latestAppointment.date)
          .startOf("day")
          .isBefore(today.add(1, "day")),
    );
    const hasRecord = matter.medicalTreatmentRecords.some(
      (record) =>
        record.medicalTreatmentId === treatment.id &&
        Boolean(record.receivedDate),
    );
    const hasBill = matter.medicalTreatmentBills.some(
      (bill) =>
        bill.medicalTreatmentId === bill.id && Boolean(bill.recordReceivedDate),
    );

    return hasPastAppointment && hasRecord && hasBill;
  }).length;
}

export function getDemandChecklist(
  matter: Matter,
  latestDemand: MatterSettlementDemand | undefined,
) {
  const summary = getTreatmentSummary(matter);

  return [
    {
      label: "All medical records received",
      done:
        summary.providersCount > 0 &&
        summary.recordsReceived === summary.providersCount,
    },
    {
      label: "All medical bills received",
      done:
        summary.providersCount > 0 &&
        summary.billsReceived === summary.providersCount,
    },
    {
      label: "Treatment substantially complete",
      done: getIsTreatmentSubstantiallyComplete(matter),
    },
    {
      label: "Demand letter drafted",
      done: Boolean(latestDemand),
    },
    {
      label: "Attorney review and approval",
      done: Boolean(latestDemand?.dateSent),
    },
  ];
}

export function topStatTone(daysRemaining: number | null) {
  if (daysRemaining == null) return "text-foreground";
  if (daysRemaining <= 30) return "text-destructive";
  if (daysRemaining <= 90) return "text-amber-700";
  return "text-emerald-700";
}
export function getInitials(name?: string | null) {
  if (!name) return "•";
  const parts = name.trim().split(/\s+/).filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

export function getTaskAssignee(matter: Matter, task: MatterTask) {
  return matter.userConnections.find(
    (connection) =>
      connection.taskId === task.id && connection.label === "Assignee",
  );
}

export function getSidebarTaskTone(daysUntilDue: number | null) {
  if (daysUntilDue == null) return "neutral";
  if (daysUntilDue <= 2) return "red";
  if (daysUntilDue <= 7) return "yellow";
  return "gray";
}

export function formatDueBadge(daysUntilDue: number | null) {
  if (daysUntilDue == null) return null;
  if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)}d overdue`;
  if (daysUntilDue === 0) return "Today";
  return `${daysUntilDue}d`;
}

export function formatDaysStatus(
  days: number | null,
  singularLabel: string,
  pluralLabel: string,
) {
  if (days == null) {
    return "—";
  }

  if (days < 0) {
    return `${Math.abs(days)} ${Math.abs(days) === 1 ? singularLabel : pluralLabel} overdue`;
  }

  return `${days} ${days === 1 ? singularLabel : pluralLabel}`;
}

export function formatStatuteDaysRemainingLabel(days: number | null) {
  if (days == null) {
    return null;
  }

  if (days < 0) {
    const overdueDays = Math.abs(days);
    return `${overdueDays} ${overdueDays === 1 ? "day" : "days"} overdue`;
  }

  return `${days} ${days === 1 ? "day" : "days"} remaining`;
}

export function getUserConnectionDisplayName(
  connection: ReturnType<typeof getTaskAssignee>,
) {
  if (!connection) {
    return "Unassigned";
  }

  return `${connection.firstName ?? ""} ${connection.lastName ?? ""}`.trim();
}
export function acceptedClosingDays(
  acceptedDate?: string,
  disbursementDate?: string,
) {
  if (!acceptedDate || !disbursementDate) {
    return null;
  }

  const accepted = dayjs(acceptedDate).startOf("day");
  const disbursed = dayjs(disbursementDate).startOf("day");

  if (!accepted.isValid() || !disbursed.isValid()) {
    return null;
  }

  return Math.max(disbursed.diff(accepted, "day"), 0);
}

export function getLatestDemandAmount(matter: Matter) {
  return getLatestByDate(matter.settlementDemands, (demand) => demand.dateSent)
    ?.amount;
}

export function latestOfferLikeAmount(matter: Matter) {
  return (
    getLatestByDate(matter.settlementOffers, (offer) => offer.dateReceived)
      ?.amount ??
    getLatestByDate(matter.settlementCounters, (counter) => counter.dateSent)
      ?.amount
  );
}

const MATTER_STATUS_DAY_DURATION_CATEGORIES = [
  "pre-litigation",
  "litigation",
  "closed",
  "referred",
] as const satisfies readonly MatterStatusCategoryOption[];

export type MatterStatusDayDurationCategory =
  (typeof MATTER_STATUS_DAY_DURATION_CATEGORIES)[number];

export type MatterStatusDayDuration = {
  id: string;
  label: string;
  category: MatterStatusDayDurationCategory;
  days: number;
  isCurrent?: boolean;
};

type MatterStatusDayDurationInput = Pick<
  Matter,
  "statusPhases" | "statusId" | "status" | "dateModified"
>;

export type MatterStatusDayDurationCatalogEntry = Pick<
  MatterStatus,
  "id" | "label" | "categoryId"
>;

function getMatterStatusPhaseStatusId(phase: MatterStatusPhase): string {
  return phase.statusId;
}

function getMatterStatusPhaseStatusLabel(phase: MatterStatusPhase): string {
  return phase.status.label;
}

function getMatterStatusPhaseStatusCategory(phase: MatterStatusPhase): string {
  return phase.status.categoryId;
}

function getEligibleCategoryForStatusId(
  matter: MatterStatusDayDurationInput,
  id: string,
): MatterStatusDayDurationCategory | undefined {
  for (const phase of matter.statusPhases) {
    const category = getMatterStatusPhaseStatusCategory(phase);
    if (
      getMatterStatusPhaseStatusId(phase) === id &&
      isMatterStatusDayDurationCategory(category)
    ) {
      return category;
    }
  }

  return undefined;
}

function getCategoryFromMatterStatusCatalog(
  id: string,
  catalog?: MatterStatusDayDurationCatalogEntry[],
): MatterStatusDayDurationCategory | undefined {
  const catalogEntry = catalog?.find((status) => status.id === id);

  if (
    catalogEntry &&
    isMatterStatusDayDurationCategory(catalogEntry.categoryId)
  ) {
    return catalogEntry.categoryId;
  }

  return undefined;
}

function doesActivePhaseMatchCurrentMatter(
  matter: MatterStatusDayDurationInput,
  activePhase?: MatterStatusPhase,
): boolean {
  if (!activePhase) {
    return false;
  }

  const currentStatusId = matter.statusId;
  if (
    currentStatusId &&
    getMatterStatusPhaseStatusId(activePhase) === currentStatusId
  ) {
    return true;
  }

  return (
    Boolean(matter.status.label) &&
    getMatterStatusPhaseStatusLabel(activePhase) === matter.status.label
  );
}

function isMatterStatusDayDurationCategory(
  category: string | null | undefined,
): category is MatterStatusDayDurationCategory {
  if (!category) {
    return false;
  }

  return (MATTER_STATUS_DAY_DURATION_CATEGORIES as readonly string[]).includes(
    category,
  );
}

/** Same open phase used for "days in status" on the matter overview header. */
export function getActiveMatterStatusPhase(
  matter: Pick<Matter, "statusPhases" | "statusId" | "status">,
): MatterStatusPhase | undefined {
  const openPhases = matter.statusPhases.filter((phase) => !phase.dateTimeEnd);

  if (openPhases.length === 0) {
    return undefined;
  }

  const currentStatusId = matter.statusId;
  if (currentStatusId) {
    const matchingId = openPhases.find(
      (phase) => getMatterStatusPhaseStatusId(phase) === currentStatusId,
    );
    if (matchingId) {
      return matchingId;
    }
  }

  if (matter.status.label) {
    const matchingLabel = openPhases.find(
      (phase) => getMatterStatusPhaseStatusLabel(phase) === matter.status.label,
    );
    if (matchingLabel) {
      return matchingLabel;
    }
  }

  return openPhases[0];
}

function isStaleOpenMatterStatusPhase(
  matter: MatterStatusDayDurationInput,
  phase: MatterStatusPhase,
): boolean {
  if (phase.dateTimeEnd) {
    return false;
  }

  if (!matter.statusId && !matter.status.id && !matter.status.label) {
    return false;
  }

  return !doesActivePhaseMatchCurrentMatter(matter, phase);
}

function getPhaseDurationReferenceDate(
  phase: Pick<MatterStatusPhase, "dateTimeEnd">,
  historicalReferenceDate: dayjs.Dayjs,
): dayjs.Dayjs {
  if (!phase.dateTimeEnd) {
    return dayjs();
  }

  return historicalReferenceDate;
}

function resolveCurrentMatterStatusCategory(
  matter: MatterStatusDayDurationInput,
  activePhase: MatterStatusPhase | undefined,
  catalog?: MatterStatusDayDurationCatalogEntry[],
): MatterStatusDayDurationCategory | undefined {
  const currentStatusId = matter.statusId;

  if (currentStatusId) {
    const fromCatalog = getCategoryFromMatterStatusCatalog(
      currentStatusId,
      catalog,
    );
    if (fromCatalog) {
      return fromCatalog;
    }

    const fromPhases = getEligibleCategoryForStatusId(matter, currentStatusId);
    if (fromPhases) {
      return fromPhases;
    }
  }

  if (doesActivePhaseMatchCurrentMatter(matter, activePhase)) {
    const activePhaseCategory = activePhase
      ? getMatterStatusPhaseStatusCategory(activePhase)
      : undefined;
    if (isMatterStatusDayDurationCategory(activePhaseCategory)) {
      return activePhaseCategory;
    }
  }

  if (isMatterStatusDayDurationCategory(matter.status.categoryId)) {
    return matter.status.categoryId;
  }

  return undefined;
}

function getMatterStatusPhaseDurationDays(
  phase: Pick<MatterStatusPhase, "dateTimeStart" | "dateTimeEnd">,
  referenceDate: dayjs.Dayjs,
): number | null {
  const start = dayjs(phase.dateTimeStart);
  if (!start.isValid()) {
    return null;
  }

  const end = phase.dateTimeEnd ? dayjs(phase.dateTimeEnd) : referenceDate;
  if (!end.isValid()) {
    return null;
  }

  const durationDays = end.diff(start, "day", true);
  if (durationDays < 0) {
    return null;
  }

  return durationDays;
}

export function formatMatterStatusDurationDays(days: number): string {
  if (days > 0 && days < 0.05) {
    return "<0.1d";
  }

  if (days < 1) {
    return `${days.toFixed(1)}d`;
  }

  const rounded = Math.round(days * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}d` : `${rounded.toFixed(1)}d`;
}

const DEFAULT_STATUS_BAR_MIN_SEGMENT_PERCENT = 5;

/**
 * Bar/label layout widths that sum to 100%. Segments shorter than
 * `minPercentPerSegment` receive a minimum slice so tiny durations stay visible
 * without overflowing the track.
 */
export function getStatusBarDisplayWidthPercents(
  segmentDays: number[],
  options?: {
    minPercentPerSegment?: number;
  },
): number[] {
  const segmentCount = segmentDays.length;
  if (segmentCount === 0) {
    return [];
  }

  if (segmentCount === 1) {
    return [100];
  }

  const minPercent = Math.min(
    options?.minPercentPerSegment ?? DEFAULT_STATUS_BAR_MIN_SEGMENT_PERCENT,
    100 / segmentCount,
  );
  const totalDays = segmentDays.reduce((sum, days) => sum + days, 0);

  if (totalDays <= 0) {
    return segmentDays.map(() => 100 / segmentCount);
  }

  const rawPercents = segmentDays.map((days) => (days / totalDays) * 100);
  const needsMinimum = rawPercents.map((percent) => percent < minPercent);
  const minimumSegmentCount = needsMinimum.filter(Boolean).length;

  if (minimumSegmentCount === 0) {
    return rawPercents;
  }

  const reservedPercent = minimumSegmentCount * minPercent;
  if (reservedPercent >= 100) {
    return segmentDays.map(() => 100 / segmentCount);
  }

  const flexSegmentIndexes = rawPercents
    .map((_, index) => index)
    .filter((index) => !needsMinimum[index]);
  const flexWeightTotal = flexSegmentIndexes.reduce(
    (sum, index) => sum + rawPercents[index],
    0,
  );
  const remainingPercent = 100 - reservedPercent;

  return rawPercents.map((rawPercent, index) => {
    if (needsMinimum[index]) {
      return minPercent;
    }

    if (flexSegmentIndexes.length === 0) {
      return remainingPercent / segmentCount;
    }

    return flexWeightTotal > 0
      ? (rawPercent / flexWeightTotal) * remainingPercent
      : remainingPercent / flexSegmentIndexes.length;
  });
}

function resolveMatterStatusPhaseCategory(
  matter: MatterStatusDayDurationInput,
  phase: MatterStatusPhase,
  catalog?: MatterStatusDayDurationCatalogEntry[],
): MatterStatusCategoryOption | undefined {
  const phaseStatusId = getMatterStatusPhaseStatusId(phase);
  const currentStatusId = matter.statusId;

  if (phaseStatusId === currentStatusId) {
    const fromCatalog = getCategoryFromMatterStatusCatalog(
      phaseStatusId,
      catalog,
    );
    if (fromCatalog) {
      return fromCatalog;
    }

    const fromPhases = getEligibleCategoryForStatusId(matter, phaseStatusId);
    if (fromPhases) {
      return fromPhases;
    }

    if (isMatterStatusDayDurationCategory(matter.status.categoryId)) {
      return matter.status.categoryId;
    }
  }

  const phaseCategory = getMatterStatusPhaseStatusCategory(phase);
  if (isMatterStatusDayDurationCategory(phaseCategory)) {
    return phaseCategory;
  }

  const fromCatalog = getCategoryFromMatterStatusCatalog(
    phaseStatusId,
    catalog,
  );
  if (fromCatalog) {
    return fromCatalog;
  }

  return undefined;
}

function resolveMatterStatusPhaseLabel(
  matter: MatterStatusDayDurationInput,
  phase: MatterStatusPhase,
): string {
  const currentStatusId = matter.statusId;
  if (
    getMatterStatusPhaseStatusId(phase) === currentStatusId &&
    matter.status.label
  ) {
    return matter.status.label;
  }

  return getMatterStatusPhaseStatusLabel(phase);
}

function getCurrentMatterStatusPhaseStart(
  matter: MatterStatusDayDurationInput,
) {
  const latestEndedPhase = getLatestByDate(
    matter.statusPhases.filter((phase) => phase.dateTimeEnd),
    (phase) => phase.dateTimeEnd,
  );

  if (latestEndedPhase?.dateTimeEnd) {
    return latestEndedPhase.dateTimeEnd;
  }

  if (
    typeof matter.dateModified === "string" &&
    dayjs(matter.dateModified).isValid()
  ) {
    return matter.dateModified;
  }

  return dayjs().toISOString();
}

function ensureCurrentMatterStatusDuration(
  matter: MatterStatusDayDurationInput,
  durationsByStatusId: Map<
    string,
    MatterStatusDayDuration & { firstEnteredAt: number }
  >,
  includedOpenPhaseForCurrentStatus: boolean,
  catalog?: MatterStatusDayDurationCatalogEntry[],
) {
  const id = matter.statusId;
  const label = matter.status.label;
  if (!id || !label) {
    return;
  }

  const activePhase = getActiveMatterStatusPhase(matter);
  const category = resolveCurrentMatterStatusCategory(
    matter,
    activePhase,
    catalog,
  );
  if (!category) {
    return;
  }

  const currentStintStart = doesActivePhaseMatchCurrentMatter(
    matter,
    activePhase,
  )
    ? activePhase?.dateTimeStart
    : getCurrentMatterStatusPhaseStart(matter);
  if (!currentStintStart) {
    return;
  }
  const currentStintDays = Math.max(
    getMatterStatusPhaseDurationDays(
      {
        dateTimeStart: currentStintStart,
        dateTimeEnd: undefined,
      },
      dayjs(),
    ) ?? 0,
    0,
  );

  const existing = durationsByStatusId.get(id);

  if (existing) {
    existing.isCurrent = true;
    existing.label = label;
    existing.category = category;

    if (!includedOpenPhaseForCurrentStatus) {
      existing.days += currentStintDays;
    } else if (doesActivePhaseMatchCurrentMatter(matter, activePhase)) {
      existing.days = Math.max(existing.days, currentStintDays);
    }

    return;
  }

  durationsByStatusId.set(id, {
    id,
    label,
    category,
    days: currentStintDays,
    isCurrent: true,
    firstEnteredAt: dayjs(currentStintStart).valueOf(),
  });
}

/**
 * Days spent in each matter status, aggregated across `statusPhases` records.
 * Uses fractional days from phase timestamps (not calendar-day rounding).
 * Only includes statuses in pre-litigation, litigation, closed, and referred.
 * The matter's current status is always included when eligible. Segments are
 * ordered chronologically, with the current status last.
 */
export function getMatterStatusDayDurations(
  matter: MatterStatusDayDurationInput,
  options?: {
    referenceDate?: string | Date | dayjs.Dayjs;
    matterStatusCatalog?: MatterStatusDayDurationCatalogEntry[];
  },
): MatterStatusDayDuration[] {
  const referenceDate = options?.referenceDate
    ? dayjs(options.referenceDate)
    : dayjs();
  const matterStatusCatalog = options?.matterStatusCatalog;
  const durationsByStatusId = new Map<
    string,
    MatterStatusDayDuration & { firstEnteredAt: number }
  >();
  let includedOpenPhaseForCurrentStatus = false;

  for (const phase of matter.statusPhases) {
    const phaseStatusId = getMatterStatusPhaseStatusId(phase);

    if (isStaleOpenMatterStatusPhase(matter, phase)) {
      continue;
    }

    const phaseCategory = resolveMatterStatusPhaseCategory(
      matter,
      phase,
      matterStatusCatalog,
    );
    if (!phaseCategory || !isMatterStatusDayDurationCategory(phaseCategory)) {
      continue;
    }

    const days = getMatterStatusPhaseDurationDays(
      phase,
      getPhaseDurationReferenceDate(phase, referenceDate),
    );
    if (days == null) {
      continue;
    }

    const phaseStart = dayjs(phase.dateTimeStart).valueOf();
    const isCurrent =
      !phase.dateTimeEnd && doesActivePhaseMatchCurrentMatter(matter, phase);
    const existing = durationsByStatusId.get(phaseStatusId);

    if (existing) {
      existing.days += days;
      existing.firstEnteredAt = Math.min(existing.firstEnteredAt, phaseStart);
      if (isCurrent) {
        existing.isCurrent = true;
        includedOpenPhaseForCurrentStatus = true;
      }
      existing.label = resolveMatterStatusPhaseLabel(matter, phase);
      existing.category = phaseCategory;
      continue;
    }

    if (isCurrent) {
      includedOpenPhaseForCurrentStatus = true;
    }

    durationsByStatusId.set(phaseStatusId, {
      id: phaseStatusId,
      label: resolveMatterStatusPhaseLabel(matter, phase),
      category: phaseCategory,
      days,
      ...(isCurrent ? { isCurrent: true } : {}),
      firstEnteredAt: phaseStart,
    });
  }

  ensureCurrentMatterStatusDuration(
    matter,
    durationsByStatusId,
    includedOpenPhaseForCurrentStatus,
    matterStatusCatalog,
  );

  const currentStatusId = matter.statusId;
  if (currentStatusId && !durationsByStatusId.has(currentStatusId)) {
    ensureCurrentMatterStatusDuration(
      matter,
      durationsByStatusId,
      false,
      matterStatusCatalog,
    );
  }

  return [...durationsByStatusId.values()]
    .sort((a, b) => {
      if (Boolean(a.isCurrent) !== Boolean(b.isCurrent)) {
        return a.isCurrent ? 1 : -1;
      }

      return a.firstEnteredAt - b.firstEnteredAt;
    })
    .map(({ ...duration }) => duration);
}
